import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import { NavigationHeader } from '../../components/ui/NavigationHeader';
import QuoteHeaderSection from './components/QuoteHeaderSection';
import LicensesSection from './components/LicensesSection';
import ImplementationSection from './components/ImplementationSection';
import TotalsSection from './components/TotalsSection';
import NotesSection from './components/NotesSection';
import PreviewModal from './components/PreviewModal';
import CustomerInfoSection from './components/CustomerInfoSection';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useProducts } from '../../contexts/ProductContext';
import { QuoteHeader, QuoteLine, ImplementationLine, QuoteTotals } from './types';
import { quoteService, QuoteLineInsert } from '../../services/quoteService';
import SendQuoteModal from './components/SendQuoteModal';
import { supabase } from '../../lib/supabase';
import { generateQuotePDF } from '../../utils/pdfGenerator';


const QuoteEditorInterface = () => {
  const navigate = useNavigate();
  const { id: editQuoteId } = useParams<{ id: string }>(); // Changed from useSearchParams to useParams

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentQuoteId, setCurrentQuoteId] = useState<string | undefined>(editQuoteId);
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalLines, setOriginalLines] = useState<{ id: string; type: 'license' | 'implementation' }[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(undefined);
  const [customerDetails, setCustomerDetails] = useState<{
    company: string;
    email: string;
    phone: string;
    address: string;
  } | null>(null);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const { getLicenseProducts, getImplementationProducts } = useProducts();

  const [onDemandRate, setOnDemandRate] = useState(130);
  const [projectMgmtRate, setProjectMgmtRate] = useState(140);

  const [quoteReference, setQuoteReference] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [quoteDate, setQuoteDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [includeLicenses, setIncludeLicenses] = useState(true);
  const [includeImplementation, setIncludeImplementation] = useState(true);
  const [licenseItems, setLicenseItems] = useState<QuoteLine[]>([]);
  const [implementationItems, setImplementationItems] = useState<ImplementationLine[]>([]);
  const [notes, setNotes] = useState('');

  // Load existing quote data when in edit mode
  useEffect(() => {
    const loadQuoteForEdit = async () => {
      if (!editQuoteId) return;

      try {
        setLoading(true);
        setIsEditMode(true);

        const quoteData = await quoteService.getByIdWithCustomer(editQuoteId);
        
        if (!quoteData) {
          alert('Quote not found');
          navigate('/quotes-overview-dashboard');
          return;
        }

        const { quote: dbQuote, lines, customer } = quoteData;

        // Pre-fill header data
        setQuoteReference(dbQuote.reference_code);
        setCustomerName(dbQuote.customer_name || customer?.name);
        setProjectName(dbQuote.project_name);
        setQuoteDate(dbQuote.quote_date);
        setValidUntil(dbQuote.valid_until);
        setStatus(dbQuote.status);
        
        // Set selected customer ID for proper linking
        if (dbQuote.customer_id) {
          setSelectedCustomerId(dbQuote.customer_id);
        }

        // Load customer details
        if (customer) {
          setCustomerDetails({
            company: customer.company || '',
            email: customer.email || '',
            phone: customer.phone || '',
            address: customer 
              ? `${customer.address || ''}, ${customer.city || ''}, ${customer.postal_code || ''}, ${customer.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',')
              : '',
          });
        }

        // Pre-fill line items with ALL fields properly mapped
        const licenseLines = lines
          .filter(line => line.line_type === 'LICENSE')
          .map(line => ({
            id: line.id,
            productId: line.product_id || '',
            group: line.group_name || '',
            groupName: line.group_name || '',
            productName: line.product_name,
            unitPrice: Number(line.unit_price || 0),
            quantity: Number(line.quantity || 0),
            monthlyAmount: Number(line.monthly_amount || 0),
            annualAmount: Number(line.annual_amount || 0),
          }));

        const implLines = lines
          .filter(line => line.line_type === 'IMPLEMENTATION')
          .map(line => ({
            id: line.id,
            productId: line.product_id || '',
            group: line.group_name || '',
            groupName: line.group_name || '',
            productName: line.product_name,
            unitPrice: Number(line.hourly_rate || 0),
            hourlyRate: Number(line.hourly_rate || 0),
            quantity: Number(line.hours || 0),
            hours: Number(line.hours || 0),
            monthlyAmount: 0,
            annualAmount: Number(line.total_amount || 0),
          }));

        setLicenseItems(licenseLines);
        setImplementationItems(implLines);

        // Track original line IDs for proper updates
        setOriginalLines([
          ...licenseLines.map(l => ({ id: l.id, type: 'license' as const })),
          ...implLines.map(l => ({ id: l.id, type: 'implementation' as const })),
        ]);

        // Pre-fill other fields with safe number handling
        setIncludeLicenses(dbQuote.licenses_included);
        setIncludeImplementation(dbQuote.implementation_included);
        setOnDemandRate(Number(dbQuote.on_demand_rate) || 130);
        setProjectMgmtRate(Number(dbQuote.project_mgmt_rate) || 140);
        setNotes(dbQuote.notes || '');

      } catch (error) {
        console.error('Error loading quote for edit:', error);
        alert('Failed to load quote data');
        navigate('/quotes-overview-dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadQuoteForEdit();
  }, [editQuoteId, navigate]);

  // Initialize dates for new quote creation
  useEffect(() => {
    if (!editQuoteId && !quoteDate) {
      // Set today's date for quote_date
      const today = new Date().toISOString().split('T')[0];
      setQuoteDate(today);
      
      // Set valid_until to today + 30 days
      const validDate = new Date();
      validDate.setDate(validDate.getDate() + 30);
      setValidUntil(validDate.toISOString().split('T')[0]);
    }
  }, [editQuoteId, quoteDate]);

  const calculateTotals = (): QuoteTotals => {
    let licensesMonthly = 0;
    let licensesAnnual = 0;
    let implementationSubtotal = 0;

    if (includeLicenses) {
      licenseItems.forEach((line) => {
        const unitPrice = Number(line.unitPrice) || 0;
        const quantity = Number(line.quantity) || 0;
        const monthly = unitPrice * quantity;
        licensesMonthly += monthly;
        licensesAnnual += monthly * 12;
      });
    }

    if (includeImplementation) {
      implementationItems.forEach((line) => {
        const hourlyRate = Number(line.unitPrice) || Number(line.hourlyRate) || 0;
        const hours = Number(line.quantity) || Number(line.hours) || 0;
        implementationSubtotal += hourlyRate * hours;
      });
    }

    // Calculate total hours from all implementation service lines with safe handling
    const totalHours = implementationItems.reduce((sum, line) => {
      const hours = Number(line.quantity) || Number(line.hours) || 0;
      return sum + hours;
    }, 0);

    // Safe rate handling
    const safeOnDemandRate = Number(onDemandRate) || 0;
    const safeProjectMgmtRate = Number(projectMgmtRate) || 0;

    // On Demand Services: 15% of total hours
    const onDemandHours = totalHours * 0.15;
    const onDemandTotal = onDemandHours * safeOnDemandRate;

    // Project Management: 15% of (total hours + on demand hours)
    const projectMgmtHours = (totalHours + onDemandHours) * 0.15;
    const projectMgmtTotal = projectMgmtHours * safeProjectMgmtRate;

    // Total Implementation Services includes subtotal + on demand + project management
    const implementationTotal = includeImplementation 
      ? implementationSubtotal + onDemandTotal + projectMgmtTotal 
      : 0;

    const totalExVAT = licensesAnnual + implementationTotal;

    return {
      licensesMonthly,
      licensesAnnual,
      implementationTotal,
      totalExVAT,
    };
  };

  const handleUpdateHeader = (header: QuoteHeader) => {
    setQuoteReference(header.referenceCode);
    setCustomerName(header.customerName);
    setProjectName(header.projectName);
    setQuoteDate(header.quoteDate);
    setValidUntil(header.validUntil);
    setStatus(header.status);
  };

  const handleCustomerSelect = (customerId: string, customerName: string) => {
    setSelectedCustomerId(customerId);
    setCustomerName(customerName);
    
    // Load customer details for display
    const loadCustomerDetails = async () => {
      try {
        const { customerService } = await import('../../services/customerService');
        const customer = await customerService.getCustomerById(customerId);
        if (customer) {
          setCustomerDetails({
            company: customer.company || '',
            email: customer.email || '',
            phone: customer.phone || '',
            address: `${customer.address || ''}, ${customer.city || ''}, ${customer.postal_code || ''}, ${customer.country || ''}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ','),
          });
        }
      } catch (error) {
        console.error('Error loading customer details:', error);
      }
    };
    
    loadCustomerDetails();
  };

  const handleToggleLicensesIncluded = (included: boolean) => {
    setIncludeLicenses(included);
  };

  const handleUpdateLicenseLine = (lineId: string, updates: Partial<QuoteLine>) => {
    setLicenseItems((prev) => 
      prev.map((line) => {
        if (line.id === lineId) {
          const updated = { ...line, ...updates };
          // Ensure both group and groupName are set
          if (updates.group !== undefined) {
            updated.groupName = updates.group;
          }
          if (updates.groupName !== undefined) {
            updated.group = updates.groupName;
          }
          // Safe calculation
          const unitPrice = Number(updated.unitPrice) || 0;
          const quantity = Number(updated.quantity) || 0;
          updated.monthlyAmount = unitPrice * quantity;
          updated.annualAmount = updated.monthlyAmount * 12;
          return updated;
        }
        return line;
      })
    );
  };

  const handleAddLicenseLine = () => {
    const newLine: QuoteLine = {
      id: Date.now().toString(),
      productId: '',
      group: '',
      groupName: '',
      productName: '',
      unitPrice: 0,
      quantity: 1,
      monthlyAmount: 0,
      annualAmount: 0,
    };
    setLicenseItems([...licenseItems, newLine]);
  };

  const handleRemoveLicenseLine = (lineId: string) => {
    setLicenseItems((prev) => prev.filter((line) => line.id !== lineId));
  };

  const handleToggleImplementationIncluded = (included: boolean) => {
    setIncludeImplementation(included);
  };

  const handleUpdateImplementationLine = (lineId: string, updates: Partial<ImplementationLine>) => {
    setImplementationItems((prev) => 
      prev.map((line) => {
        if (line.id === lineId) {
          const updated = { ...line, ...updates };
          // Ensure both group and groupName are set
          if (updates.group !== undefined) {
            updated.groupName = updates.group;
          }
          if (updates.groupName !== undefined) {
            updated.group = updates.groupName;
          }
          // Sync hourlyRate with unitPrice
          if (updates.hourlyRate !== undefined) {
            updated.unitPrice = updates.hourlyRate;
          }
          if (updates.unitPrice !== undefined) {
            updated.hourlyRate = updates.unitPrice;
          }
          // Sync hours with quantity
          if (updates.hours !== undefined) {
            updated.quantity = updates.hours;
          }
          if (updates.quantity !== undefined) {
            updated.hours = updates.quantity;
          }
          // Safe calculation
          const hourlyRate = Number(updated.hourlyRate) || Number(updated.unitPrice) || 0;
          const hours = Number(updated.hours) || Number(updated.quantity) || 0;
          updated.annualAmount = hourlyRate * hours;
          return updated;
        }
        return line;
      })
    );
  };

  const handleAddImplementationLine = () => {
    const newLine: ImplementationLine = {
      id: Date.now().toString(),
      productId: '',
      group: '',
      groupName: '',
      productName: '',
      unitPrice: 0,
      hourlyRate: 0,
      quantity: 0,
      hours: 0,
      monthlyAmount: 0,
      annualAmount: 0,
    };
    setImplementationItems([...implementationItems, newLine]);
  };

  const handleRemoveImplementationLine = (lineId: string) => {
    setImplementationItems((prev) => prev.filter((line) => line.id !== lineId));
  };

  const handleUpdateNotes = (notes: string) => {
    setNotes(notes);
  };

  const handleSave = async () => {
    // Validate required fields
    if (!selectedCustomerId) {
      setError('Please select a customer');
      return;
    }

    if (!projectName || projectName.trim() === '') {
      setError('Please enter a project name');
      return;
    }

    if (!status) {
      setError('Please select a status');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const quoteData = {
        reference_code: quoteReference || undefined,
        customer_id: selectedCustomerId,
        customer_name: customerName,
        project_name: projectName,
        status: status,
        quote_date: quoteDate || undefined,
        valid_until: validUntil || undefined,
        notes: notes,
        licenses_included: includeLicenses,
        implementation_included: includeImplementation,
        on_demand_rate: Number(onDemandRate) || 0,
        project_mgmt_rate: Number(projectMgmtRate) || 0,
      };

      let quote;
      if (isEditMode && editQuoteId) {
        // UPDATE existing quote
        quote = await quoteService.update(editQuoteId, quoteData);

        // Handle line items intelligently
        const currentLicenseIds = licenseItems.filter(item => item.id).map(item => item.id);
        const currentImplIds = implementationItems.filter(item => item.id).map(item => item.id);
        
        const linesToDelete = originalLines.filter(original => {
          if (original.type === 'license') {
            return !currentLicenseIds.includes(original.id);
          } else {
            return !currentImplIds.includes(original.id);
          }
        });

        for (const line of linesToDelete) {
          await quoteService.deleteLine(line.id);
        }

        for (let i = 0; i < licenseItems.length; i++) {
          const item = licenseItems[i];
          const lineData = {
            quote_id: quote.id,
            line_type: 'LICENSE' as const,
            group_name: item.group,
            product_name: item.productName,
            unit_price: item.unitPrice,
            quantity: item.quantity,
            monthly_amount: item.monthlyAmount,
            annual_amount: item.annualAmount,
            display_order: i,
          };

          if (item.id && item.id.length > 20) {
            await quoteService.updateLine(item.id, lineData);
          } else {
            await quoteService.addLine(lineData);
          }
        }

        for (let i = 0; i < implementationItems.length; i++) {
          const item = implementationItems[i];
          const lineData = {
            quote_id: quote.id,
            line_type: 'IMPLEMENTATION' as const,
            group_name: item.group,
            product_id: item.productId,
            product_name: item.productName,
            hourly_rate: item.unitPrice,
            hours: item.quantity,
            total_amount: item.annualAmount,
            display_order: i,
          };

          if (item.id && item.id.length > 20) {
            await quoteService.updateLine(item.id, lineData);
          } else {
            await quoteService.addLine(lineData);
          }
        }

        setSuccessMessage('Quote updated successfully!');
      } else {
        // CREATE new quote
        quote = await quoteService.create(quoteData);

        for (let i = 0; i < licenseItems.length; i++) {
          const line = licenseItems[i];
          const lineInsert: QuoteLineInsert = {
            quote_id: quote.id,
            line_type: 'LICENSE',
            group_name: line.group,
            product_id: line.productId,
            product_name: line.productName,
            unit_price: line.unitPrice,
            quantity: line.quantity,
            monthly_amount: line.monthlyAmount,
            annual_amount: line.annualAmount,
            display_order: i,
          };
          await quoteService.addLine(lineInsert);
        }

        for (let i = 0; i < implementationItems.length; i++) {
          const line = implementationItems[i];
          const lineInsert: QuoteLineInsert = {
            quote_id: quote.id,
            line_type: 'IMPLEMENTATION',
            group_name: line.group,
            product_id: line.productId,
            product_name: line.productName,
            hourly_rate: line.unitPrice,
            hours: line.quantity,
            total_amount: line.annualAmount,
            display_order: i,
          };
          await quoteService.addLine(lineInsert);
        }

        setSuccessMessage('Quote created successfully!');
      }

      // Show success message briefly then navigate
      setTimeout(() => {
        navigate(`/quotes-overview-dashboard`);
      }, 1500);

    } catch (error) {
      console.error('Error saving quote:', error);
      setError(error instanceof Error ? error.message : 'Failed to save quote. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendToCustomer = () => {
    // Validate that quote has been saved
    if (!currentQuoteId && !editQuoteId) {
      alert('Please save the quote first before sending');
      return;
    }

    // Validate that customer is selected
    if (!selectedCustomerId || !customerDetails?.email) {
      alert('Please select a customer before sending');
      return;
    }

    setIsSendModalOpen(true);
  };

  const handleDuplicateQuote = async () => {
    // Validate that we're in edit mode with a valid quote ID
    if (!editQuoteId) {
      setError('Please save the quote first before duplicating');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Generate new reference code using Supabase RPC
      const { data: newReferenceCode, error: rpcError } = await supabase.rpc('generate_quote_reference');
      
      if (rpcError) {
        throw new Error('Failed to generate reference code: ' + rpcError.message);
      }

      if (!newReferenceCode) {
        throw new Error('No reference code returned from database');
      }

      // Duplicate the quote using the service
      const duplicatedQuote = await quoteService.duplicateQuote(editQuoteId, newReferenceCode);

      setSuccessMessage('Quote duplicated successfully!');

      // Navigate to the new duplicated quote after a brief delay
      setTimeout(() => {
        navigate(`/quote-editor-interface/${duplicatedQuote.id}`);
        // Reload the page to show the new quote data
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error duplicating quote:', error);
      setError(error instanceof Error ? error.message : 'Failed to duplicate quote. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintQuote = async () => {
    setIsPrinting(true);
    try {
      const t = calculateTotals();

      const licenseLineItems = licenseItems.map((item) => ({
        id: item.id || '',
        group: item.group || '',
        productName: item.productName || '',
        unitPrice: Number(item.unitPrice) || 0,
        quantity: Number(item.quantity) || 0,
        monthlyAmount: (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0),
        annualAmount: (Number(item.unitPrice) || 0) * (Number(item.quantity) || 0) * 12,
      }));

      const implLineItems = implementationItems.map((item) => ({
        id: item.id || '',
        group: item.group || '',
        productName: item.productName || '',
        unitPrice: Number(item.unitPrice) || Number(item.hourlyRate) || 0,
        quantity: Number(item.quantity) || Number(item.hours) || 0,
        monthlyAmount: 0,
        annualAmount: (Number(item.unitPrice) || Number(item.hourlyRate) || 0) * (Number(item.quantity) || Number(item.hours) || 0),
      }));

      const quoteForPDF = {
        id: currentQuoteId || editQuoteId || '',
        referenceCode: quoteReference || 'DRAFT',
        customerName: customerName,
        projectName: projectName,
        status: (status as 'OPEN' | 'APPROVED' | 'CANCELLED') || 'OPEN',
        createdDate: quoteDate || new Date().toISOString().split('T')[0],
        validUntil: validUntil || new Date().toISOString().split('T')[0],
        customerInfo: {
          name: customerName,
          company: customerDetails?.company || '',
          email: customerDetails?.email || '',
          phone: customerDetails?.phone || '',
          address: customerDetails?.address || '',
        },
        licenses: {
          included: includeLicenses,
          items: licenseLineItems,
          monthlySubtotal: t.licensesMonthly,
          annualSubtotal: t.licensesAnnual,
        },
        implementation: {
          included: includeImplementation,
          items: implLineItems,
          monthlySubtotal: 0,
          annualSubtotal: t.implementationTotal,
        },
        onDemandTotal: 0,
        projectMgmtTotal: 0,
        notes: notes,
        totalMonthly: t.licensesMonthly,
        totalAnnual: t.licensesAnnual,
        totalExVAT: t.totalExVAT,
      };

      await generateQuotePDF(quoteForPDF);
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsPrinting(false);
    }
  };

  const totals = calculateTotals();

  // Create quoteData object for PreviewModal from current state
  const quoteData = {
    header: {
      referenceCode: quoteReference,
      customerName: customerName,
      projectName: projectName,
      status: status,
      quoteDate: quoteDate,
      validUntil: validUntil,
    },
    licenses: {
      included: includeLicenses,
      lines: licenseItems,
    },
    implementation: {
      included: includeImplementation,
      lines: implementationItems,
    },
    notes: notes,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading quote data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={isSidebarCollapsed} onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <NavigationHeader />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-6 space-y-6">
            {successMessage && (
              <div className="bg-success/10 border border-success/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <p className="text-sm text-success font-medium">{successMessage}</p>
              </div>
            )}

            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <p className="text-sm text-error font-medium">{error}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {isEditMode ? 'Edit Quote' : 'Create New Quote'}
                </h1>
                <p className="text-sm text-text-secondary mt-1">
                  Create and manage comprehensive quotes with real-time calculations
                </p>
              </div>
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/quotes-overview-dashboard')}
              >
                Back to Overview
              </Button>
            </div>

            <QuoteHeaderSection
              header={{
                referenceCode: quoteReference,
                customerName: customerName,
                projectName: projectName,
                status: status,
                quoteDate: quoteDate,
                validUntil: validUntil,
              }}
              selectedCustomerId={selectedCustomerId}
              onUpdate={handleUpdateHeader}
              onCustomerSelect={handleCustomerSelect}
              onSave={handleSave}
              onPreview={() => setIsPreviewOpen(true)}
              isSaving={isSaving}
            />

            {customerDetails && (
              <CustomerInfoSection 
                customerName={customerName}
                customerDetails={customerDetails}
              />
            )}

            <LicensesSection
              included={includeLicenses}
              lines={licenseItems}
              products={getLicenseProducts()}
              onToggleIncluded={handleToggleLicensesIncluded}
              onUpdateLine={handleUpdateLicenseLine}
              onAddLine={handleAddLicenseLine}
              onRemoveLine={handleRemoveLicenseLine}
              monthlySubtotal={totals.licensesMonthly}
              annualSubtotal={totals.licensesAnnual}
            />

            <ImplementationSection
              included={includeImplementation}
              lines={implementationItems}
              products={getImplementationProducts()}
              onToggleIncluded={handleToggleImplementationIncluded}
              onUpdateLine={handleUpdateImplementationLine}
              onAddLine={handleAddImplementationLine}
              onRemoveLine={handleRemoveImplementationLine}
              subtotal={implementationItems.reduce((sum, line) => {
                const hourlyRate = Number(line.unitPrice) || Number(line.hourlyRate) || 0;
                const hours = Number(line.quantity) || Number(line.hours) || 0;
                return sum + (hourlyRate * hours);
              }, 0)}
              onDemandRate={onDemandRate}
              projectMgmtRate={projectMgmtRate}
              onUpdateOnDemandRate={setOnDemandRate}
              onUpdateProjectMgmtRate={setProjectMgmtRate}
            />

            <TotalsSection
              totals={totals}
              licensesIncluded={includeLicenses}
              implementationIncluded={includeImplementation}
            />

            <NotesSection notes={notes} onUpdate={handleUpdateNotes} />

            <div className="flex items-center justify-end gap-3 p-4 bg-card border border-border rounded-lg">
              <Button 
                variant="outline" 
                iconName="Copy" 
                iconPosition="left"
                disabled={isSaving || !editQuoteId}
                onClick={handleDuplicateQuote}
              >
                Duplicate Quote
              </Button>
              <Button
                variant="outline"
                iconName="Send"
                iconPosition="left"
                onClick={handleSendToCustomer}
                disabled={isSaving}
              >
                Send to Customer
              </Button>
              <Button
                variant="default"
                iconName={isSaving ? "Loader" : "Save"}
                iconPosition="left"
                onClick={handleSave}
                disabled={isSaving}
                className={isSaving ? "opacity-75" : ""}
              >
                {isSaving ? 'Saving...' : 'Save Quote'}
              </Button>
              <Button
                variant="default"
                iconName={isPrinting ? "Loader" : "Printer"}
                iconPosition="left"
                onClick={handlePrintQuote}
                disabled={isPrinting || isSaving}
                className={isPrinting ? "opacity-75" : ""}
              >
                {isPrinting ? 'Generating...' : 'Print Quote'}
              </Button>
            </div>
          </div>
        </main>
      </div>

      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        quoteData={quoteData}
        totals={totals}
      />

      <SendQuoteModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        quote={
          currentQuoteId || editQuoteId
            ? {
                id: currentQuoteId || editQuoteId || '',
                referenceCode: quoteReference,
                customerName: customerName,
                customerEmail: customerDetails?.email || '',
                companyName: customerDetails?.company || '',
                projectName: projectName,
                totalExVAT: totals.totalExVAT,
                validUntil: validUntil,
              }
            : null
        }
      />
    </div>
  );
};

export default QuoteEditorInterface;